<template>
  <div>
    <!-- Menu Button -->
    <button
      @click="toggleMenu"
      class="absolute left-2 top-1/2 transform -translate-y-1/2 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-main text-white shadow-lg hover:bg-main-hover duration-300"
    >
      <Menu v-if="!isOpen" class="w-5 h-5" />
      <X v-else class="w-5 h-5" />
    </button>

    <!-- Sliding Menu -->
    <div
      class="h-screen fixed top-0 left-0 w-full bg-white shadow-lg z-40 transform transition-transform duration-300"
      :class="{ '-translate-x-full': !isOpen, 'translate-x-0': isOpen }"
    >
      <div class="p-4 flex flex-col justify-between h-full">
        <!-- Menu Items -->
        <div>
          <h2 class="text-lg font-bold text-main mb-4 text-center">
            Menu
          </h2>
          <ul>
            <li
              v-for="item in headers"
              :key="item.route"
              class="mb-2"
            >
              <!-- Parent Menu Item -->
              <div class="flex items-center justify-between py-2 px-4 bg-gray-100 rounded-lg hover:bg-gray-200 duration-300">
                <router-link
                  :to="item.route"
                  class="text-main font-semibold flex-1"
                  @click="closeMenu"
                >
                  {{ item.title }}
                </router-link>
                <ChevronDown
                  v-if="item.children && item.children.length"
                  class="w-4 h-4 text-main cursor-pointer transition-transform duration-300"
                  :class="{ 'rotate-180': openSubMenu === item.route }"
                  @click.stop="toggleSubMenu(item)"
                />
              </div>

              <!-- Submenu with Animation -->
              <transition name="submenu">
                <ul
                  v-if="item.children && item.children.length && openSubMenu === item.route"
                  class="mt-2 space-y-1"
                >
                  <li
                    v-for="child in item.children"
                    :key="child.route"
                    class="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-lg hover:bg-gray-100 duration-300"
                  >
                    <router-link
                      :to="child.route"
                      class="text-gray-700 font-medium flex-1 flex items-center space-x-2"
                      @click="closeMenu"
                    >
                      <ChevronRight class="w-4 h-4 text-gray-500" />
                      <span>{{ child.title }}</span>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </li>
          </ul>
        </div>

        <!-- User Profile Section -->
        <div
          class="flex items-center p-4 bg-gray-100 rounded-lg shadow-md hover:bg-gray-200 duration-300"
          @click="goToProfile"
        >
          <!-- User Icon -->
          <Avatar size="16" :user="user" textSize="text-3xl"/>
          <!-- User Info -->
          <div class="ml-3 flex-1">
            <p class="text-sm font-semibold text-main">{{ user?.firstname }} {{ user?.lastname }}</p>
            <p class="text-xs text-gray-500">{{ user?.email }}</p>
            <button
              class="mt-2 text-sm text-red-500 font-bold py-1 px-2 rounded-lg hover:bg-red-500 hover:text-white duration-300"
              @click.stop="logout"
            >
              <Translate value="logout" class="w-full"/>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay -->
    <BackPanel
      v-if="isOpen"
      class="!z-30 !h-screen"
      @click="closeMenu"
    ></BackPanel>
  </div>
</template>

<script>
import Avatar from "@/components/Utils/Avatar.vue";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-vue-next";
import BackPanel from "@/components/Blocks/BackPanel.vue";

export default {
  name: "MList",
  components: {
    Menu,
    X,
    ChevronDown,
    ChevronRight,
    Avatar,
    BackPanel
  },
  props: {
    headers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
      openSubMenu: null, // Tracks which submenu is open
      user: null, // User data
    };
  },
  methods: {
    toggleMenu() {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) {
        this.openSubMenu = null; // Close all submenus when the menu is closed
      }
    },
    closeMenu() {
      this.isOpen = false;
      this.openSubMenu = null;
    },
    toggleSubMenu(item) {
      this.openSubMenu = this.openSubMenu === item.route ? null : item.route;
    },
    updateUser() {
      this.user = this.$userStore.user;
    },
    logout() {
      this.$userStore.logout();
      this.$router.push('/login');
      this.$toast.add({
        summary: this.Translate('logged-out'),
        detail: this.Translate('logout-success'),
        severity: 'success',
        life: 3000
      });
    },
    goToProfile() {
      if(!this.$userStore.isAdmin()) return;
      this.$router.push("/profile");
      this.closeMenu();
    },
    Translate(key) {
      return this.$languageStore.getTranslation(key);
    }
  },
  mounted() {
    this.updateUser();
  },
  watch: {
    $route() {
      this.updateUser();
    },
  },
};
</script>

<style scoped>
/* Add smooth transition for submenu rotation */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

/* Submenu Animation */
.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.3s ease;
}
.submenu-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.submenu-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.submenu-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.submenu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>